import React, { useId } from 'react';
import Select, { MultiValue, SingleValue, StylesConfig, GroupBase } from 'react-select';
import AsyncSelect from 'react-select/async';
import styles from './FormCard.module.css';
import { PageFrame, PageHeader, Surface, Button, Placeholder } from '@/components/ui';

export const FormGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles['form-grid']}>{children}</div>
);

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  /** Secondary line under the control — live values, units, context. Not for errors. */
  hint?: React.ReactNode;
  maxWidth?: string;
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({ label, required, error, hint, maxWidth, children }) => {
  const id = useId();
  const child = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<{ id?: string }>, { id })
    : children;
  return (
    <div className={styles['field-group']} style={maxWidth ? { maxWidth } : undefined}>
      <label className={styles.label} htmlFor={id}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      {child}
      {error && <span className={styles['field-error']}>{error}</span>}
      {hint && <span className={styles.hint}>{hint}</span>}
    </div>
  );
};

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
}

export const FormInput: React.FC<FormInputProps> = ({ hasError, className, ...rest }) => (
  <input
    className={`${styles.input}${hasError ? ` ${styles['input-error']}` : ''}${className ? ` ${className}` : ''}`}
    {...rest}
  />
);

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  hasError?: boolean;
}

export const FormSelect: React.FC<FormSelectProps> = ({ hasError, className, children, ...rest }) => (
  <select
    className={`${styles.input} ${styles.select}${hasError ? ` ${styles['input-error']}` : ''}${className ? ` ${className}` : ''}`}
    {...rest}
  >
    {children}
  </select>
);

export type SelectOption = { value: number; label: string };
export type SelectOptionGroup = GroupBase<SelectOption> & { label: string; options: SelectOption[] };

/**
 * react-select styling that matches `.input` above. react-select writes inline
 * styles, so theme tokens resolve here like in a stylesheet —
 * never hard-code a colour.
 *
 * Styling that matches `.input` above, so a searchable select and a
 * plain FormInput sit at the same height and weight inside one FormGrid.
 */
const reactSelectStyles = <
  IsMulti extends boolean,
  Group extends GroupBase<SelectOption> = GroupBase<SelectOption>,
>(
  hasError?: boolean,
): StylesConfig<SelectOption, IsMulti, Group> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '34px',
    borderRadius: '10px',
    fontSize: '0.95rem',
    backgroundColor: state.isDisabled ? 'var(--surface-2)' : 'var(--surface)',
    borderColor: hasError ? 'var(--neg)' : state.isFocused ? 'var(--navy-600)' : 'var(--border-strong)',
    boxShadow: state.isFocused
      ? `0 0 0 3px ${hasError ? 'var(--neg-line)' : 'var(--focus-ring)'}`
      : 'none',
    '&:hover': { borderColor: state.isFocused ? 'var(--navy-600)' : 'var(--accent-line)' },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 10px', maxHeight: '120px', overflowY: 'auto' }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.95rem',
    backgroundColor: state.isSelected ? 'var(--accent)' : state.isFocused ? 'var(--surface-3)' : 'var(--surface)',
    color: state.isSelected ? 'var(--accent-ink)' : 'var(--text)',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: 'var(--surface-3)', borderRadius: 'var(--r-pill)', paddingLeft: '4px' }),
  multiValueLabel: (base) => ({ ...base, color: 'var(--navy-700)', fontSize: '0.85rem', fontWeight: 600 }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--navy-700)',
    borderRadius: 'var(--r-pill)',
    '&:hover': { backgroundColor: 'var(--navy-100)', color: 'var(--accent)' },
  }),
  groupHeading: (base) => ({
    ...base,
    color: 'var(--text-faint)',
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    paddingTop: '8px',
    paddingBottom: '4px',
  }),
  placeholder: (base) => ({ ...base, color: 'var(--text-faint)', fontSize: '0.95rem' }),
  singleValue: (base) => ({ ...base, color: 'var(--text)', fontSize: '0.95rem' }),
  input: (base) => ({ ...base, fontSize: '0.95rem' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

interface FormReactSelectProps {
  /** Injected by FormField; forwarded as react-select's inputId so the label targets the input. */
  id?: string;
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (selected: SingleValue<SelectOption>) => void;
  placeholder?: string;
  hasError?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
}

/** Searchable single select. Use instead of FormSelect when the list is long. */
export const FormReactSelect: React.FC<FormReactSelectProps> = ({
  id,
  hasError,
  isClearable = true,
  placeholder = 'Select…',
  ...rest
}) => (
  <Select<SelectOption, false>
    inputId={id}
    styles={reactSelectStyles<false>(hasError)}
    menuPortalTarget={document.body}
    placeholder={placeholder}
    isClearable={isClearable}
    {...rest}
  />
);

interface FormReactMultiSelectProps {
  id?: string;
  options: SelectOption[] | SelectOptionGroup[];
  value: SelectOption[];
  onChange: (selected: MultiValue<SelectOption>) => void;
  placeholder?: string;
  hasError?: boolean;
  isDisabled?: boolean;
  isOptionDisabled?: (option: SelectOption) => boolean;
}

/** Searchable multi select, rendering selections as removable chips. */
export const FormReactMultiSelect: React.FC<FormReactMultiSelectProps> = ({
  id,
  hasError,
  placeholder = 'Select…',
  ...rest
}) => (
  <Select<SelectOption, true, SelectOptionGroup>
    isMulti
    inputId={id}
    styles={reactSelectStyles<true, SelectOptionGroup>(hasError)}
    menuPortalTarget={document.body}
    placeholder={placeholder}
    closeMenuOnSelect={false}
    {...rest}
  />
);

interface FormReactAsyncSelectProps {
  id?: string;
  /** Called per keystroke; returns the matching options. */
  loadOptions: (inputValue: string) => Promise<SelectOption[]>;
  value: SelectOption | null;
  onChange: (selected: SingleValue<SelectOption>) => void;
  placeholder?: string;
  hasError?: boolean;
  isDisabled?: boolean;
}

/** Server-searched single select, for lists too large to send to the browser up front. */
export const FormReactAsyncSelect: React.FC<FormReactAsyncSelectProps> = ({
  id,
  hasError,
  placeholder = 'Search…',
  ...rest
}) => (
  <AsyncSelect<SelectOption, false>
    inputId={id}
    styles={reactSelectStyles<false>(hasError)}
    menuPortalTarget={document.body}
    placeholder={placeholder}
    defaultOptions
    isClearable
    {...rest}
  />
);

/**
 * Red text inside a FormField `hint`. For a hint that reports a problem with
 * supporting data (a price lookup that came back empty), not with the user's
 * input — that is what FormField's `error` is for.
 */
export const FormHintError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className={styles['hint-error']}>{children}</span>
);

/**
 * Stand-in for a control that has nothing to show yet — options still loading, or
 * a prerequisite field unset. Sits in the FormField slot where the control goes.
 */
export const FormFieldNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles['field-note']}>{children}</div>
);

/** Checkbox grid — lays out a set of FormCheckbox items in the same column rhythm as FormGrid. */
export const FormCheckboxGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles['checkbox-grid']}>{children}</div>
);

interface FormCheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string;
}

export const FormCheckbox: React.FC<FormCheckboxProps> = ({ label, hint, className, ...rest }) => (
  <label className={`${styles['checkbox-label']}${className ? ` ${className}` : ''}`}>
    <input type="checkbox" className={styles.checkbox} {...rest} />
    <span className={styles['checkbox-text']}>
      <span>{label}</span>
      {hint && <span className={styles['checkbox-hint']}>{hint}</span>}
    </span>
  </label>
);

interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

export const FormSection: React.FC<FormSectionProps> = ({ title, description, children }) => (
  <div className={styles.section}>
    <h2 className={styles['section-title']}>{title}</h2>
    {description && <p className={styles['section-desc']}>{description}</p>}
    {children}
  </div>
);

interface FormCardProps {
  title: string;
  subtitle?: string;
  backLabel?: string;
  onBack?: () => void;
  errorBanner?: string;
  submitLabel: string;
  cancelLabel?: string;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submitting?: boolean;
  loading?: boolean;
  loadingMessage?: string;
  /**
   * 'compact' (default) — narrow centred card for short forms.
   * 'page' — full-width card matching the list pages (1600px, 80vh min-height).
   */
  variant?: 'compact' | 'page';
  children: React.ReactNode;
}

const FormCard: React.FC<FormCardProps> = ({
  title,
  subtitle,
  backLabel = '← Back',
  onBack,
  errorBanner,
  submitLabel,
  cancelLabel = 'Cancel',
  onSubmit,
  onCancel,
  submitting = false,
  loading = false,
  loadingMessage = 'Loading…',
  variant = 'compact',
  children,
}) => {
  // The header renders while loading too, so the page keeps its shape and does
  // not jump when the data lands. Bailing out to bare text dropped the frame
  // entirely and left the reader on an otherwise empty screen.
  const frame = (body: React.ReactNode) => (
    <PageFrame
      header={
        <PageHeader
          title={title}
          subtitle={subtitle}
          actions={onBack ? <Button onBand onClick={onBack}>{backLabel}</Button> : undefined}
        />
      }
    >
      {body}
    </PageFrame>
  );

  if (loading) {
    return frame(
      <Surface className={variant === 'compact' ? styles['card-compact'] : undefined}>
        <Placeholder kind="loading" message={loadingMessage} />
      </Surface>,
    );
  }

  return frame(
      <Surface
        padded
        fill
        className={variant === 'compact' ? styles['card-compact'] : undefined}
      >
        {errorBanner && <div className={styles['error-banner']}>{errorBanner}</div>}

        <form onSubmit={onSubmit} noValidate>
          {children}

          <div className={styles.footer}>
            <Button type="button" onClick={onCancel} disabled={submitting}>
              {cancelLabel}
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </Surface>,
  );
};

export default FormCard;